import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { IchraSelectReimbursementProductRequest, selectIchraReimbursementProduct } from 'core'
import ReimbursementProduct from 'core/domain/benefit/reimbursementProduct'

import { CommandResponse } from 'common/hooks/useCommandRequestV2'

import { Col, Radio, Row } from 'common/components'

import { BaseComponentProps } from '../../Common'

import './styles.less'

export type SetEligibleExpensesProps = {
  productClassId: string
  reimbursementProduct?: number
}

type FormValues = {
  reimbursementProduct?: number
}

type PlansType = {
  planType: number
  title: string
  description: string
}

const plans: PlansType[] = [
  {
    planType: ReimbursementProduct.PremiumPlusOutOfPocketExpenses,
    title: 'Premiums + Expenses',
    description: 'Reimburse any IRS-qualified out-of-pocket medical expenses, including insurance premiums.',
  },
  {
    planType: ReimbursementProduct.PremiumsOnly,
    title: 'Premiums Only',
    description: 'Reimburse only qualified individual medical, dental, and vision insurance premiums.',
  },
]

const SetEligibleExpenses = (
  props: BaseComponentProps<SetEligibleExpensesProps> & {
    commandHandler?: (values: any) => Promise<CommandResponse<void>>
  },
) => {
  const { t: translate } = useTranslation()
  const { bindSubmitFormHandler, commandHandler } = props
  const [initialValues] = useState<FormValues>({
    reimbursementProduct: props.state.reimbursementProduct,
  })

  const handleSubmit = async (values: FormValues) => {
    const request: IchraSelectReimbursementProductRequest = {
      productClassId: props.state.productClassId,
      reimbursementProduct: values.reimbursementProduct,
    }

    const result = await commandHandler(request)
    if (result.completed) {
      props.goNext()
    } else {
      props.setIsSubmiting(false)
    }
  }

  useEffect(() => {
    props.setIsFormValid(formValidator(translate).isValidSync(initialValues))
  }, [initialValues])

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={(values) => {
        const isValid: boolean = formValidator(translate).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        return {}
      }}
      validateOnChange={true}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form autoComplete="off" onSubmit={handleSubmit} className="select_class">
            <Row>
              <Col span={24}>
                <h5 className="select_class_info mb-24">
                  Should this class reimburse all IRS-qualified medical expenses, OR be restricted to reimbursing only
                  qualified individual insurance premiums?
                </h5>
              </Col>
            </Row>

            <Radio.Group
              name="reimbursementProduct"
              className="ichra-reimbursement-plan-option"
              defaultValue={formik.values['reimbursementProduct']}
            >
              {plans.map((item) => (
                <Radio.Button
                  key={item.planType}
                  value={item.planType}
                  contentAlign="start"
                  size="small"
                  className="mb-16"
                >
                  <div>
                    <Row>
                      <Col span={24}>
                        <h5 className="mb-8">{item.title}</h5>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <p className="color-gray-7">{item.description}</p>
                      </Col>
                    </Row>
                  </div>
                </Radio.Button>
              ))}
            </Radio.Group>
          </form>
        )
      }}
    </Formik>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    reimbursementProduct: Yup.number().required(
      translate('admin-dashboard.benefit-details.class-design.qsehra.reimburse-product.error'),
    ),
  })

export default SetEligibleExpenses
